import { validateTvEnv } from '@streambase/config/tv';
import branding from '../../config/branding';

const tvEnv = validateTvEnv();
export const DEVELOPMENT_MODE = tvEnv.NODE_ENV !== 'production';

// REACT_APP_API_URL (webpack-inlined) overrides the branding-derived base so
// local/dev stacks work; a trailing /api is stripped so it is never doubled.
// Same seam as mobile's packages/mobile/src/services/api.ts.
const API_BASE = tvEnv.REACT_APP_API_URL.replace(/\/api$/, '') || `https://api.${branding.domain}`;

export const API_ENDPOINT = `${API_BASE}/api`;
export const AKAMAI_ENDPOINT = `https://${branding.cdnDomain}/`;
export const SOCKET_ENDPOINT = API_BASE;

export const GetHeaders = async (token?: string | boolean) => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return { headers };
};

export const ServerFetch = async (
  url: string,
  options: RequestInit,
  authenticated?: string | boolean,
) => {
  const { headers } = await GetHeaders(authenticated);
  try {
    const response = await fetch(url, { ...options, headers });
    // Interceptar 401 Unauthorized - sessão expirada
    if (response.status === 401) {
      console.warn('ServerFetch: 401 Unauthorized', url);
      return { error: true, status: 401, message: 'Unauthorized' };
    }
    try {
      return await response.json();
    } catch (err) {
      console.log('ServerParseError', err, url, { ...options, headers });
      return response;
    }
  } catch (error) {
    console.log('ServerFetchError', error);
    return false;
  }
};

export const GET = async (path: string, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'GET',
    },
    authenticated,
  );
};

export const POST = async (
  path: string,
  body: unknown,
  authenticated: string | boolean = false,
) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    authenticated,
  );
};

export const PUT = async (path: string, body: unknown, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
    authenticated,
  );
};

export const DELETE = async (path: string, authenticated: string | boolean = false) => {
  return await ServerFetch(
    `${API_ENDPOINT}${path}`,
    {
      method: 'DELETE',
    },
    authenticated,
  );
};

export const ReadAddressesByZipCode = async (zipCode: string) => {
  try {
    const result = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    return result.json();
  } catch (err) {
    return false;
  }
};

export const ResolveUri = (result: Record<string, unknown> | string | null): string | null => {
  const obj = typeof result === 'object' && result !== null ? result : null;
  const field = (key: string): string =>
    typeof obj?.[key] === 'string' ? (obj[key] as string) : '';
  const path: string | null =
    field('path') ||
    field('fileCopyUri') ||
    field('uri') ||
    field('croppedImage') ||
    (typeof result === 'string' ? result : null);
  return path && path.indexOf('file://') === -1 ? `file://${path}` : path;
};

export const PrepareFile = (result: Record<string, unknown> | string | null) => {
  const uri = ResolveUri(result) ?? '';
  const name = uri.split('/').pop() as string;
  const match = /\.(\w+)$/.exec(name);
  const type = match ? `image/${match[1]}` : `image`;
  return { uri, name, type };
};

export const ServerUploadImage = async (result: Record<string, unknown> | string | null) => {
  return await PostImage(PrepareFile(result));
};

export const PostImage = async (fileToUpload: { uri: string; name: string; type: string }) => {
  const formData = new FormData();
  // React Native FormData accepts a {uri,name,type} part; the DOM lib types only
  // allow Blob|string, so cast to satisfy tsc without changing the RN runtime.
  formData.append('files', fileToUpload as unknown as Blob, fileToUpload.name);
  const { headers } = await GetHeaders(true);
  delete headers['Content-Type'];
  const response = await fetch(`${API_ENDPOINT}/upload`, {
    method: 'POST',
    body: formData,
    headers,
  });
  const responseJson = await response.json();
  return responseJson?.[0];
};
